/**
 * What a press on the board starts, by what is under it (read from the element: `data-item`,
 * `data-frame-title`, `data-handle`, `data-rotate`, `data-arrow`) and the tool chosen:
 *
 * - select: an item or a frame's title selects it (Shift or Ctrl adds) and drags the selection
 *   (snapping to the others' edges; Alt: no snapping); a click on the one item already selected
 *   writes its words there (Miro's second click); a handle or a side of the selection's outline
 *   resizes it (a picture keeps its own proportions, Shift frees them; anything else the other
 *   way round), the round one turns it; the background draws a marquee selecting what it
 *   touches; an arrow selects it (Alt+click deletes it). A frame's own background is the board's
 *   for a marquee, but a click on it selects the frame, and once selected it drags it.
 * - hand, the middle button, or Space held: pans.
 * - note, text, shape, frame: a click puts one there at its usual size, a drag draws it.
 * - arrow: from one item to another links them.
 * - a board mode's handle (`data-mode-drag=<item id>`, drawn by a GUI pack's overlay: UI/UX's
 *   prototype mode): dragged to the pointer and handed to the mode where it is let go.
 * - image: a click asks for pictures from the computer.
 * - pen: a drag draws a line by hand (one item, one undo); the pen stays chosen, as in Miro.
 * - eraser: a drag rubs out the drawn lines it passes over (hidden at once, removed on release, one undo).
 * - while a picture is cropped (`data-crop-handle`, `data-crop-box`): a handle moves a side or
 *   corner of the part kept, a press inside it slides the picture under it, a press elsewhere
 *   keeps the crop and ends it.
 *
 * Moves start past a few pixels, so a click stays a click. Everything ends in `up`.
 */
import { createSignal } from "solid-js";
import type { Link } from "@protocol/Link";
import { frameAround, touched, type Board, type Handle, type Point, type Rect } from "../../../state/board";
import type { ModeDrag } from "../../../state/packs";
import { DRAG_PX } from "../constants";

/** What the gestures need besides the board. */
export interface GestureDeps {
  board: Board;
  /** Selects an arrow (`null`: nothing). */
  selectLink(link: Link | null): void;
  /** Removes an arrow (Alt+click). */
  removeLink(link: Link): void;
  /** The arrow at `index` in the board's list. */
  arrowAt(index: number): Link | undefined;
  /** Whether this page may change the board (else only select and pan). */
  canEdit(): boolean;
  /** Asks for pictures from the computer, to put at `at` with `tool` (a tool drawing pictures). */
  pickPictures(at: Point, tool: string): void;
  /** Starts writing an item's words in place (`at`: the page point clicked, where the caret goes). */
  edit(id: string, at?: Point): void;
  /** Whether item `id` has words written in place (a note, text, a shape...; not a picture). */
  writable(id: string): boolean;
  /** The proportions (width / height) the selection keeps when resized: one picture's own, else none (resized freely). */
  pictureRatio(): number | null;
  /** A board mode's drag from item `from`, let go at `at` (board units): handed to the mode in use. */
  dropModeDrag(from: string, at: Point): Promise<void>;
}

/** A press in progress. */
type Press =
  | { type: "pan"; last: Point }
  | { type: "move"; start: Point; started: boolean; write: string | null }
  | { type: "resize"; start: Point; handle: Handle; picture: boolean }
  | { type: "crop"; start: Point; handle: Handle | "move" }
  | { type: "rotate" }
  | { type: "marquee"; start: Point; add: boolean; frame: string | null }
  | { type: "draw"; start: Point }
  | { type: "arrow"; from: string; start: Point }
  | { type: "mode"; from: string }
  | { type: "ink" }
  | { type: "erase" };

/** The board's pointer gestures. `point` turns an event into screen pixels of the area. */
export function createGestures(deps: GestureDeps, point: (e: MouseEvent) => Point) {
  const { board } = deps;
  let press: Press | null = null;
  /** Space is held: the background pans whatever the tool. */
  let space = false;
  /** The marquee being drawn, in board units. */
  const [marquee, setMarquee] = createSignal<Rect | null>(null);
  /** The rectangle a drawing tool is drawing, in board units. */
  const [drawing, setDrawing] = createSignal<Rect | null>(null);
  /** The arrow being drawn: from an item's centre to the pointer, in board units. */
  const [linking, setLinking] = createSignal<{ from: string; to: Point } | null>(null);
  /** The drag from a board mode's handle: from an item to the pointer, in board units. */
  const [modeDrag, setModeDrag] = createSignal<ModeDrag | null>(null);
  /** The line being drawn with the pen, in board units. */
  const [ink, setInk] = createSignal<Point[] | null>(null);
  /** The drawn lines the eraser passed over, hidden until it is let go. */
  const [erased, setErased] = createSignal<ReadonlySet<string>>(new Set());

  /** Adds the drawn line under the page point of `e`, if any, to those rubbed out. */
  const rub = (e: PointerEvent) => {
    for (const el of document.elementsFromPoint(e.clientX, e.clientY)) {
      const id = el.closest<HTMLElement>("[data-drawing]")?.dataset.drawing;
      if (id && !erased().has(id)) setErased(new Set([...erased(), id]));
    }
  };

  /** The board point under an event. */
  const world = (e: MouseEvent) => {
    const p = point(e);
    return board.toWorld(p.x, p.y);
  };

  /** What the element under the press says it is. */
  const under = (e: PointerEvent) => {
    const el = e.target as Element;
    const dragged = el.closest<HTMLElement>("[data-mode-drag]")?.dataset.modeDrag;
    if (dragged) return { type: "mode" as const, id: dragged };
    const handle = el.closest<HTMLElement>("[data-handle]")?.dataset.handle as Handle | undefined;
    if (handle) return { type: "handle" as const, handle };
    if (el.closest("[data-rotate]")) return { type: "rotate" as const };
    const item = el.closest<HTMLElement>("[data-item]")?.dataset.item;
    if (item) return { type: "item" as const, id: item };
    const frame = el.closest<HTMLElement>("[data-frame-title]")?.dataset.frameTitle;
    if (frame) return { type: "item" as const, id: frame };
    const arrow = el.closest<SVGElement>("[data-arrow]")?.dataset.arrow;
    if (arrow !== undefined) return { type: "arrow" as const, index: Number(arrow) };
    return { type: "background" as const };
  };

  return {
    marquee,
    drawing,
    linking,
    modeDrag,
    ink,
    erased,
    /** Space pressed or let go (panning with the left button, as in every drawing tool). */
    setSpace(on: boolean) {
      space = on;
    },
    down(e: PointerEvent) {
      const p = point(e);
      const at = board.toWorld(p.x, p.y);
      const hit = under(e);
      const tool = board.tool();
      if (e.button === 1 || tool === "hand" || (space && e.button === 0)) return void (press = { type: "pan", last: p });
      if (e.button !== 0) return;
      const edit = deps.canEdit();
      if (board.cropping()) {
        const el = e.target as Element;
        const handle = el.closest<HTMLElement>("[data-crop-handle]")?.dataset.cropHandle as Handle | undefined;
        if (handle) return void (press = { type: "crop", start: p, handle });
        if (el.closest("[data-crop-box]")) return void (press = { type: "crop", start: p, handle: "move" });
        return void board.endCrop(true);
      }
      if (hit.type === "mode" && edit) return void (press = { type: "mode", from: hit.id });
      if (hit.type === "handle" && edit) {
        const ratio = deps.pictureRatio();
        board.beginResize(hit.handle, ratio ?? undefined);
        return void (press = { type: "resize", start: at, handle: hit.handle, picture: ratio !== null });
      }
      if (hit.type === "rotate" && edit) {
        board.beginRotate();
        return void (press = { type: "rotate" });
      }
      if (tool === "arrow" && edit) {
        if (hit.type === "item") return void (press = { type: "arrow", from: hit.id, start: at });
        return;
      }
      const drawn = board.toolInfo(tool)?.element;
      if (drawn === "image" && edit) return void deps.pickPictures(at, tool);
      if (drawn === "drawing" && edit) return void ((press = { type: "ink" }), setInk([at]));
      if (tool === "eraser" && edit) {
        press = { type: "erase" };
        return rub(e);
      }
      if (tool !== "select" && edit) return void ((press = { type: "draw", start: at }), setDrawing({ ...at, w: 0, h: 0 }));
      if (hit.type === "arrow") {
        const link = deps.arrowAt(hit.index);
        if (link && e.altKey && edit) return deps.removeLink(link);
        return deps.selectLink(link ?? null);
      }
      if (hit.type === "item") {
        const add = e.shiftKey || e.ctrlKey || e.metaKey;
        // The one item selected already: a click (not a drag) writes its words.
        const only = board.selected().length === 1 && board.selected()[0] === hit.id;
        if (add || !board.selected().includes(hit.id)) board.select([hit.id], add);
        if (!add && edit) {
          board.beginMove();
          press = { type: "move", start: p, started: false, write: only && deps.writable(hit.id) ? hit.id : null };
        }
        return;
      }
      // On a frame's background: a click selects the frame, a drag of the selected frame moves it.
      const frame = frameAround(board.content(), { ...at, w: 0, h: 0 });
      const add = e.shiftKey || e.ctrlKey || e.metaKey;
      if (frame && !add && edit && board.selected().length === 1 && board.selected()[0] === frame) {
        board.beginMove();
        return void (press = { type: "move", start: p, started: false, write: null });
      }
      press = { type: "marquee", start: at, add, frame };
      setMarquee({ ...at, w: 0, h: 0 });
    },
    move(e: PointerEvent) {
      if (!press) return;
      const p = point(e);
      const at = board.toWorld(p.x, p.y);
      switch (press.type) {
        case "pan":
          board.pan(p.x - press.last.x, p.y - press.last.y);
          press.last = p;
          return;
        case "move": {
          if (!press.started && Math.hypot(p.x - press.start.x, p.y - press.start.y) < DRAG_PX) return;
          press.started = true;
          const k = board.camera().k;
          return board.moveBy((p.x - press.start.x) / k, (p.y - press.start.y) / k, e.altKey);
        }
        case "resize":
          // A picture keeps its proportions unless Shift is held; anything else only with Shift.
          return board.resizeBy(at.x - press.start.x, at.y - press.start.y, press.picture !== e.shiftKey);
        case "crop": {
          const k = board.camera().k;
          return board.cropBy(press.handle, (p.x - press.start.x) / k, (p.y - press.start.y) / k);
        }
        case "rotate":
          return board.rotateTowards(at, e.shiftKey);
        case "marquee":
          return void setMarquee({ x: press.start.x, y: press.start.y, w: at.x - press.start.x, h: at.y - press.start.y });
        case "draw":
          return void setDrawing({ x: press.start.x, y: press.start.y, w: at.x - press.start.x, h: at.y - press.start.y });
        case "arrow":
          return void setLinking({ from: press.from, to: at });
        case "mode":
          return void setModeDrag({ from: press.from, to: at });
        case "ink": {
          const line = ink();
          if (!line) return;
          const last = line[line.length - 1]!;
          // A point every screen pixel or so is plenty: the line is simplified on release anyway.
          if (Math.hypot(at.x - last.x, at.y - last.y) * board.camera().k < 1) return;
          return void setInk([...line, at]);
        }
        case "erase":
          return rub(e);
      }
    },
    async up(e: PointerEvent) {
      const done = press;
      press = null;
      if (!done) return;
      const at = world(e);
      switch (done.type) {
        case "move":
        case "resize":
        case "rotate":
          if (done.type === "move" && !done.started) {
            board.cancel();
            if (done.write) deps.edit(done.write, { x: e.clientX, y: e.clientY });
          } else await board.end();
          return;
        case "crop":
          return board.cropPressEnd();
        case "marquee": {
          const m = marquee();
          setMarquee(null);
          if (!m) return;
          const k = board.camera().k;
          if (Math.abs(m.w) * k < DRAG_PX && Math.abs(m.h) * k < DRAG_PX) {
            if (done.frame) return board.select([done.frame]);
            if (!done.add) board.select([]);
            deps.selectLink(null);
            return;
          }
          const caught = touched(board.content(), m);
          board.select(done.add ? caught.filter((id) => !board.selected().includes(id)) : caught, done.add);
          return;
        }
        case "draw": {
          const r = drawing();
          setDrawing(null);
          if (!r) return;
          const k = board.camera().k;
          const click = Math.abs(r.w) * k < DRAG_PX * 2 && Math.abs(r.h) * k < DRAG_PX * 2;
          const tool = board.tool();
          const id = await board.create(tool, click ? { x: at.x, y: at.y, w: 0, h: 0 } : r);
          // Back to selecting, as Miro does after one item; words are written at once.
          board.setTool("select");
          if (id && tool !== "frame") deps.edit(id);
          return;
        }
        case "arrow": {
          setLinking(null);
          const target = (document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>("[data-item]"))?.dataset.item;
          if (target && target !== done.from) await board.connect(done.from, target);
          board.setTool("select");
          return;
        }
        case "ink": {
          const line = ink();
          await board.draw(line ?? []);
          setInk(null);
          return;
        }
        case "erase": {
          const ids = [...erased()];
          await board.erase(ids);
          setErased(new Set<string>());
          return;
        }
        case "mode": {
          const drawn = modeDrag();
          setModeDrag(null);
          if (!drawn) return;
          await deps.dropModeDrag(done.from, at);
          return;
        }
      }
    },
    /** The wheel zooms around the pointer; with Shift it pans sideways, a trackpad's two fingers pan. */
    wheel(e: WheelEvent) {
      e.preventDefault();
      const p = point(e);
      if (e.ctrlKey || e.metaKey || Math.abs(e.deltaY) >= 50 || e.deltaMode !== 0) {
        if (e.shiftKey) return board.pan(-e.deltaY, 0);
        return board.zoomAt(p.x, p.y, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)));
      }
      board.pan(-e.deltaX, -e.deltaY);
    },
    /** Escape: drops the gesture in progress; `true` when there was one. */
    cancel(): boolean {
      const had = press !== null;
      press = null;
      setMarquee(null);
      setDrawing(null);
      setLinking(null);
      setModeDrag(null);
      setInk(null);
      setErased(new Set<string>());
      return board.cancel() || had;
    },
  };
}

/** The gestures of one board view. */
export type Gestures = ReturnType<typeof createGestures>;
